import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Icon, Typography, TextInputHelper, Button, LoadingCircle } from '@packages/ui/shared';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { docsService, vinSessionService } from '@web/services/singletons';
import ReactDOM from 'react-dom';
import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import { publicationsActions } from './context/publications.state';
import { usePublicationsDispatch } from './context/publications.hooks';
import { StisDocument } from '@web/vin-sessions/types';
import { PublicationType } from './publication-type-icons';
import { openDocumentTab, resolveDocumentUrl } from '@web/vin-sessions/utils/open-document-tab';
import { useAuthState } from '@packages/contexts/auth';
import { getSaveErrorMessage } from './save-error.utils';

const logger = getLogger('vin-sessions');

export interface AssociateSectionModalProps {
    show: boolean;
    onHide: () => void;
    publicationFormId: string;
    setAssociateVehicles?: React.Dispatch<React.SetStateAction<any>>;
    setAssociatePublications?: React.Dispatch<React.SetStateAction<any>>;
    associatePublications?: any;
    selectedSection: string;
    setPublicationInfo: any;
    setEditAssociateVehicles?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface CarlineType {
    id: string;
    description: string;
    [key: string]: any;
}

const useStyles = createUseStyles({
    renderTokenClass: {
        borderRadius: 4,
        paddingTop: 2,
        paddingRight: 4,
        paddingBottom: 2,
        paddingLeft: 4,
        background: '#DEE6EF',
        color: '#1F518F',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        marginRight: 4,
        fontSize: 14,
        cursor: 'pointer',
    },
    close: {
        marginLeft: 3,
        marginBottom: 1,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    dropdown: {
        '& .dropdown-menu': {
            width: 280,
            maxHeight: 300,
            padding: '8px 0 !important',
            borderRadius: `3px !important`,
            border: `1px solid ${colors.grayThree} !important`,
            boxShadow: `0px 16px 12px -12px rgba(0, 0, 0, 0.16) !important`,
            overflowY: 'auto !important',
        },
        '& .dropdown-menu::-webkit-scrollbar': {
            width: 10,
        },
        '& .dropdown-menu::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '& .dropdown-menu::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
    },
    dropdownItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px',
        marginTop: 6,
        marginLeft: 6,
        width: '100%',
    },
    clearIcon: {
        backgroundColor: colors.white,
        cursor: 'pointer',
        position: 'absolute',
        right: 8,
        top: 8,
        zIndex: 2,
    },
    documentType: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    titleContainer: {
        maxWidth: 200,
        fontSize: 15,
        color: '#007BFF',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '@media (max-width: 600px)': {
            maxWidth: 150,
        },
        '@media (max-width: 400px)': {
            maxWidth: 100,
        },
        '@media (min-width: 1440px)': {
            maxWidth: 300,
        },
    },
    scrollBar: {
        overflowY: 'scroll',
        '&::-webkit-scrollbar': {
            width: 10,
            display: 'block !important',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            cursor: 'pointer',
            backgroundColor: colors.blueOne,
        },
    },
    loadingCircleContainer: {
        minHeight: '40vh',
    },
    loaderCircleOuter: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
    },
    checkboxStyle: {
        width: 18,
        height: 18,
        position: 'relative',
        top: 3,
        left: 3,
        accentColor: '#999DA3',
    },
    flexColumnContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    marginTop8: {
        marginTop: 8,
    },
    flexGap8: {
        display: 'flex',
        gap: 8,
    },
    positionRelativeCursor: {
        position: 'relative',
        cursor: 'pointer',
    },
    cursorPointer: {
        cursor: 'pointer',
    },
    clearIconAbsolute: {
        position: 'absolute',
        right: 10,
        top: 40,
    },
    calendarFixed: {
        position: 'fixed',
        zIndex: 100,
    },
    positionRelative: {
        position: 'relative',
    },
    calendarBottomRight: {
        position: 'absolute',
        zIndex: 100,
        right: 150,
        bottom: 10,
    },
    marginRight16: {
        marginRight: 16,
        fontWeight: 500,
    },
    flexDisplay: {
        display: 'flex',
    },
    marginRight5: {
        marginRight: 5,
    },
    tooltipStyle: {
        fontSize: 16,
        padding: '8px 12px',
        textAlign: 'center',
    },
    pointerEventsNone: {
        pointerEvents: 'none',
    },
    relativeFullWidth: {
        position: 'relative',
        width: '100%',
    },
    footerJustifyContent: {
        justifyContent: 'space-between',
    },
    footerFlexEnd: {
        justifyContent: 'flex-end',
    },
    vehicleTabButton: {
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        width: 'fit-content',
        minWidth: 0,
        padding: 0,
        marginTop: 2,
    },
    vehicleTabText: {
        fontFamily: 'Roboto',
        fontWeight: 600,
        fontSize: 15,
        letterSpacing: 0,
        textAlign: 'center',
        color: '#1F518F',
    },
    calendarDynamic: {
        position: 'fixed',
        zIndex: 100,
    },
    flexGap8JustifyEnd: {
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
    },
    buttonContainer: {
        justifyContent: 'flex-end',
        position: 'absolute',
        bottom: 0,
        right: 10,
    },
    flexJustifyBetweenMarginTop: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 4,
    },
    iconContainer: {
        flex: '0 0 3.5%',
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
    },
    selectedPublicationsContainer: {
        marginTop: 16,
        maxHeight: 240,
        borderRadius: 4,
    },
    dropdownContainer: {
        position: 'absolute',
        top: 40,
        left: 0,
        width: '100%',
        background: '#fff',
        border: '1px solid #ccc',
        borderRadius: 4,
        zIndex: 10,
        maxHeight: 250,
        overflowY: 'auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
    publicationItem: {
        display: 'flex',
        alignItems: 'center',
        height: 56,
        padding: '0 12px',
        gap: 12,
    },
    publicationItemBorder: {
        borderBottom: '1px solid #eee',
    },
    publicationDocumentType: {
        flex: '0 0 25.6%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        alignItems: 'center',
        gap: 8,
    },
    marginTop2: {
        marginTop: 2,
    },
    publicationTitle: {
        flex: '0 0 30.5%',
        minWidth: 0,
        color: '#007BFF',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    publicationVersion: {
        flex: '0 0 16.6%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    publicationDate: {
        flex: '0 0 16.6%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    selectedPublicationItem: {
        display: 'flex',
        alignItems: 'center',
        height: 56,
        border: '1px solid #E3E3E3',
        borderRadius: 4,
        padding: '0 12px',
        gap: 12,
        marginTop: 8,
    },
    selectedPublicationItemBorder: {
        borderBottom: '1px solid #eee',
    },
    selectedPublicationDocumentType: {
        flex: '0 0 25.6%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        alignItems: 'center',
        gap: 8,
    },
    selectedPublicationTitle: {
        flex: '0 0 30.5%',
        minWidth: 0,
        color: '#007BFF',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    selectedPublicationVersion: {
        flex: '0 0 16.6%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    selectedPublicationDate: {
        flex: '0 0 16.6%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    addedVehiclesSection: {
        backgroundColor: '#F7F9FC',
        borderRadius: 6,
        border: '1px solid #E3E8EF',
        padding: '12px 16px',
        marginBottom: 16,
    },
    addedVehiclesSectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
    },
    addedVehiclesBadge: {
        backgroundColor: '#1F518F',
        color: '#fff',
        borderRadius: 12,
        padding: '1px 8px',
        fontSize: 12,
        fontWeight: 600,
    },
    newVehicleSection: {
        borderRadius: 6,
        border: '1px solid #DEE6EF',
        padding: '16px',
    },
    newVehicleSectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
    },
    sectionDivider: {
        border: 'none',
        borderTop: '1px dashed #C5CDD8',
        margin: '16px 0',
    },
});

export const AssociateSectionModal: FC<AssociateSectionModalProps> = ({
    show,
    onHide,
    publicationFormId,
    setAssociateVehicles,
    setAssociatePublications,
    associatePublications,
    selectedSection,
    setPublicationInfo,
    setEditAssociateVehicles,
}) => {
    const assetEditorRootRef = useRef(document.getElementById('asset-editor-root'));
    const assetEditorElementRef = useRef(document.createElement('div'));
    const classes = useStyles();
    const [confirmButtonLoading] = useState<boolean>(false);
    const { account, token } = useAuthState();
    const publicationDispatch = usePublicationsDispatch();
    const [newTextSearch, setNewTextSearch] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [publications, setPublications] = useState<StisDocument[]>([]);
    const [selectedPublications, setSelectedPublications] = useState<StisDocument[]>([]);
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    const handleCancelAction = () => {
        setPage(0);
        setTotalPages(0);
        setNewTextSearch('');
        setPublications([]);
        setSelectedPublications([]);
        onHide();
    };

    const handleConfirmPublications = useCallback(async () => {
        const body = {
            associateDocIds: selectedPublications.map((pub) => pub.documentId),
        };
        try {
            const response = await docsService.associateDocuments({
                publicationFormId,
                body,
                method: 'POST',
            });

            if (!response.success) {
                if (!response.aborted) {
                    publicationDispatch(
                        publicationsActions.setToastMessage({
                            toast: {
                                toastMessage: getSaveErrorMessage(
                                    response.data,
                                    'Something went wrong, please try again'
                                ),
                                iconColor: 'white',
                                iconName: 'x-close',
                            },
                        })
                    );
                }
                return;
            }

            if (
                response.data &&
                ((Array.isArray(response.data) && response.data.length > 0) ||
                    (typeof response.data === 'object' &&
                        !Array.isArray(response.data) &&
                        Object.keys(response.data).length > 0))
            ) {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'Publications Successfully associated',
                            iconColor: 'white',
                            iconName: 'checkmark',
                        },
                    })
                );
                if (setAssociatePublications) {
                    setAssociatePublications(response.data.associatedDocs);
                    setPublicationInfo({ associatePublicationChange: true });
                }
                onHide();
                handleCancelAction();
            } else {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'Something went wrong. Please change the selection and try again.',
                            iconColor: 'white',
                            iconName: 'x-close',
                        },
                    })
                );
            }
        } catch (error) {
            logger.error(`Failed to associate the publications to the publication [${publicationFormId}]`, {
                error,
                publicationFormId,
            });
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: 'Something went wrong, please try again',
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            );
        }
    }, [selectedPublications]);

    const formatDate = (isoDate: string) => {
        const date = new Date(isoDate);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
        });
    };

    const handleSelectPublication = useCallback(
        (result: StisDocument) => {
            const alreadySelected = associatePublications?.some((pub: any) => pub.mappedDocId === result.documentId);
            if (alreadySelected) {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'This publication is already associated',
                            iconColor: 'white',
                            iconName: 'x-close',
                        },
                    })
                );
                return;
            }
            let isSelected;
            setSelectedPublications((prev) => {
                isSelected = prev.some((pub) => pub.documentId === result.documentId);
                if (isSelected) {
                    return prev;
                } else {
                    return [...prev, result];
                }
            });
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: isSelected ? `Publication Already Associated` : `Publication Successfully Added`,
                        iconColor: 'white',
                        iconName: isSelected ? `x-close` : `checkmark`,
                    },
                })
            );
        },
        [associatePublications]
    );

    const handleRemovePublication = (result: StisDocument) => {
        setSelectedPublications((prev) => {
            return prev.filter((pub) => pub.documentId !== result.documentId);
        });
        publicationDispatch(
            publicationsActions.setToastMessage({
                toast: {
                    toastMessage: 'Publication Successfully Removed',
                    iconColor: 'white',
                    iconName: 'checkmark',
                },
            })
        );
    };

    const fetchPublications = async (searchText: string, page: number) => {
        if (searchText.length > 0) {
            setIsLoading(true);
            try {
                const res = await vinSessionService.getPublicationsByQuery({
                    uri: `?page=${page}&size=${10}`,
                    body: {
                        query: searchText,
                        docStatus: 'published',
                        searchType: 'all',
                    },
                    vin: '',
                    publicFlag: false,
                    ignoreCache: true,
                });
                if (
                    res.data &&
                    typeof res.data === 'object' &&
                    'content' in res.data &&
                    Array.isArray((res.data as any).content)
                ) {
                    const data = res.data as { content: any[]; page: { totalPages: number } };
                    setPublications((prevContent) => (page > 0 ? [...prevContent, ...data.content] : data.content));
                    setTotalPages(data.page.totalPages);
                } else {
                    setSelectedPublications([]);
                    setPublications([]);
                    setPage(0);
                    setTotalPages(0);
                }
            } catch (e) {
                setSelectedPublications([]);
                setPublications([]);
                setPage(0);
                setTotalPages(0);
            }
            setIsLoading(false);
            setShowDropdown(true);
        } else {
            setPage(0);
            setTotalPages(0);
            setSelectedPublications([]);
            setPublications([]);
            setShowDropdown(false);
        }
    };

    const handleScroll = (event: React.UIEvent<HTMLDivElement>, totalPages: number, searchText: string) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        if (scrollTop + clientHeight + 1 >= scrollHeight) {
            setPage((prev) => {
                const newPage = prev + 1;
                if (newPage >= totalPages) {
                    return prev;
                }
                fetchPublications(searchText, newPage);
                return newPage;
            });
        }
    };

    useEffect(() => {
        const rootRef = assetEditorRootRef.current;
        rootRef?.appendChild(assetEditorElementRef.current);

        return () => {
            rootRef?.removeChild(assetEditorElementRef.current);
        };
    }, []);

    return ReactDOM.createPortal(
        <Modal
            show={show}
            centered={true}
            onHide={() => {
                onHide();
                handleCancelAction();
                if (setEditAssociateVehicles) setEditAssociateVehicles(false);
            }}
            size={'lg'}
        >
            <Modal.Header closeButton={true}>
                <Modal.Title>{`Associate ${selectedSection}`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={classes.flexColumnContainer}>
                    {selectedSection === 'publications' ? (
                        <Typography variant="caption">
                            {`Search and select related publications to link with this document. You can add multiple publications as needed.`}
                        </Typography>
                    ) : (
                        <Typography variant="caption">
                            {`Select the details below to associate the document with a range of vehicles. You can add multiple VIN ranges by clicking 'Associate Vehicles' again.`}
                        </Typography>
                    )}
                </div>
                <div className={classes.marginTop8}>
                    <>
                        <Typography variant="caption3">{`Search for publications`}</Typography>
                        <div className={classes.relativeFullWidth}>
                            <TextInputHelper
                                placeholder="Search publications..."
                                value={newTextSearch}
                                onChangeText={async (text) => {
                                    setNewTextSearch(text);
                                    fetchPublications(text, page);
                                }}
                                rightIcon={
                                    isLoading ? (
                                        <LoadingCircle borderColor="#436CA1" accentColor="#DEE6EF" />
                                    ) : newTextSearch ? (
                                        <Icon
                                            name="x-close"
                                            onPress={() => {
                                                setNewTextSearch('');
                                                setPublications([]);
                                                setSelectedPublications([]);
                                                setPage(0);
                                                setTotalPages(0);
                                                setShowDropdown(false);
                                            }}
                                        />
                                    ) : (
                                        <Icon name="magnifying-glass" />
                                    )
                                }
                                onFocus={() => {
                                    if (publications.length > 0) setShowDropdown(true);
                                }}
                                onBlur={() => {
                                    setTimeout(() => setShowDropdown(false), 150);
                                }}
                            />
                            {showDropdown && publications.length > 0 && (
                                <div
                                    className={`${classes.scrollBar} ${classes.dropdownContainer}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onScroll={(event) => handleScroll(event, totalPages, newTextSearch)}
                                >
                                    {publications.map((result: any, idx: number) => (
                                        <div
                                            key={result.id || idx}
                                            className={`${classes.publicationItem} ${idx !== publications.length - 1 ? classes.publicationItemBorder : ''}`}
                                        >
                                            <div
                                                className={`${classes.documentType} ${classes.publicationDocumentType}`}
                                            >
                                                <PublicationType publicationType={result.docType} size={20} />
                                                <Typography numberOfLines={1} style={{ marginTop: 2 }}>
                                                    {result.docType}
                                                </Typography>
                                            </div>
                                            <div
                                                className={`${classes.titleContainer} ${classes.publicationTitle}`}
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await openDocumentTab(() =>
                                                        resolveDocumentUrl(result, account, token)
                                                    );
                                                }}
                                                title={result.title}
                                            >
                                                {result.title}
                                            </div>
                                            <div className={classes.publicationVersion} title={result.docCode}>
                                                <Typography numberOfLines={1}>{result.docCode}</Typography>
                                            </div>
                                            <div
                                                className={classes.publicationDate}
                                                title={formatDate(result.createdDate)}
                                            >
                                                <Typography numberOfLines={1}>
                                                    {formatDate(result.createdDate)}
                                                </Typography>
                                            </div>
                                            <div className={classes.iconContainer}>
                                                <Icon
                                                    name="add-circle"
                                                    color="blueOne"
                                                    onPress={() => handleSelectPublication(result)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={`${classes.selectedPublicationsContainer} ${classes.scrollBar}`}>
                            {selectedPublications.length > 0 &&
                                selectedPublications.map((result: any, idx: number) => (
                                    <div key={result.id || idx} className={classes.selectedPublicationItem}>
                                        <div
                                            className={`${classes.documentType} ${classes.selectedPublicationDocumentType}`}
                                        >
                                            <PublicationType publicationType={result.docType} size={20} />
                                            <Typography numberOfLines={1} style={{ marginTop: 2 }}>
                                                {result.docType}
                                            </Typography>
                                        </div>
                                        <div
                                            className={`${classes.titleContainer} ${classes.selectedPublicationTitle}`}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                await openDocumentTab(() => resolveDocumentUrl(result, account, token));
                                            }}
                                            title={result.title}
                                        >
                                            {result.title}
                                        </div>
                                        <div className={classes.selectedPublicationVersion} title={result.docCode}>
                                            <Typography numberOfLines={1}>{result.docCode}</Typography>
                                        </div>
                                        <div
                                            className={classes.selectedPublicationDate}
                                            title={formatDate(result.createdDate)}
                                        >
                                            <Typography numberOfLines={1}>{formatDate(result.createdDate)}</Typography>
                                        </div>
                                        <div className={classes.iconContainer}>
                                            <Icon
                                                name="x-close"
                                                color="blueOne"
                                                onPress={() => handleRemovePublication(result)}
                                            />
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </>
                </div>
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: 'flex-end' }}>
                <div className={classes.flexDisplay}>
                    <Button variant="ghost-blue" onPress={handleCancelAction} style={{ marginRight: 5 }}>
                        {`Cancel`}
                    </Button>

                    {selectedPublications.length == 0 ? (
                        <OverlayTrigger
                            placement="top"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <Tooltip
                                    id={'1'}
                                    style={{
                                        fontSize: 16,
                                        padding: '8px 12px',
                                        textAlign: 'center',
                                    }}
                                >
                                    {'Please select at least one publication to associate'}
                                </Tooltip>
                            }
                        >
                            <span>
                                <Button variant="primary" style={{ pointerEvents: 'none' }} disabled>
                                    {'Associate'}
                                </Button>
                            </span>
                        </OverlayTrigger>
                    ) : (
                        <Button variant="primary" onPress={handleConfirmPublications} isLoading={confirmButtonLoading}>
                            {`Associate`}
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>,
        assetEditorElementRef.current
    );
};
